import { reset } from 'redux-form';
import hash from 'object-hash';
import { compose, map, sortBy, each, filter, pickBy } from 'lodash/fp';
import { pipe } from 'fp-ts/function';
import { array, string } from 'fp-ts';
import { createAction, showErrorAlert } from '@mews/framework';
import { ApplicationState, CommanderThunkAction, CommanderThunkDispatch } from '@mews-commander/types';
import { EnterpriseImport, ResourceCategoryImport, ResourceImport } from '@mews-commander/data-access';
import { getFirstFile, getOrNull } from '@mews-commander/utils';
import { normalizeResponseData } from '@mews-commander/legacy-data-structures';
import { getSubscription } from '@mews-commander/legacy-entities/src/ducks/subscriptions';
import { getCategories } from '../selectors/categories';
import EnterpriseBuilderScreenStep from '../EnterpriseBuilderScreenStep';
import { EnterpriseFormValues, SimpleResource } from '../types';
import { REDUCERS_RESET, JSON_IMPORTED, RESOURCES_UPDATED } from './types';
import { getFileDataObject } from './helpers';
import { addCategory } from './categories';
import { addFloor } from './floors';
import { addSettings } from './settings';
import { changeStep } from './steps';

const mapEnterpriseSettings = (state: ApplicationState) =>
    map<EnterpriseImport, EnterpriseFormValues>(
        ({
            subscriptionId,
            legalEnvironmentCode,
            defaultCurrencyCode,
            defaultCultureCode,
            defaultLanguageCode,
            timeZoneId,
            pricing,
            chainId,
            address: { addressLine1, addressLine2, city, postalCode, countryCode },
            ...other
        }) => ({
            subscription: getOrNull('code', getSubscription(state, { subscriptionId })),
            chain: chainId,
            legalEnvironment: legalEnvironmentCode,
            currency: defaultCurrencyCode,
            culture: defaultCultureCode,
            language: defaultLanguageCode,
            timeZone: timeZoneId,
            pricingMode: pricing,
            addressLine1,
            addressLine2,
            city,
            postalCode,
            countryCode,
            ...other,
        })
    );

const sortCategories = compose(
    map(
        ({
            // omitted properties
            ordering,

            shortName: shortcut,
            ...other
        }) => ({
            shortcut,
            ...other,
        })
    ),
    sortBy('ordering'),
    filter('name')
);

const importCategories =
    (categories: ResourceCategoryImport[]): CommanderThunkAction =>
    (dispatch) => {
        const sortedCategories = sortCategories(categories);

        // @ts-expect-error
        each(({ id, ...otherCategory }) => dispatch(addCategory(otherCategory, id)))(sortedCategories);
    };

const importResources =
    (resourcesData: ResourceImport[]): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const categories = getCategories(state);

        const floorNumbers = pipe(
            resourcesData,
            // @ts-expect-error - missing BE annotation
            array.map((resource) => resource.data.value?.floorNumber),
            array.uniq(string.Eq)
        );

        floorNumbers.forEach((floorNumber: string) => dispatch(addFloor({ name: floorNumber, id: hash(floorNumber) })));

        each(({ index, id }) => {
            const resourceCategoryResources = pickBy({ resourceCategoryIndexes: [index] })(resourcesData);
            const resources: SimpleResource[] = [];
            each((resource) => {
                // @ts-expect-error - https://mews.myjetbrains.com/youtrack/issue/RND-2729/Switch-builder-from-ids-to-indexes
                const { id: resourceId, index: resourceIndex, data } = resource;
                resources.push({
                    floorId: hash(data.value.floorNumber),
                    categoryId: id,
                    id: resourceId,
                    index: resourceIndex,
                    // @ts-expect-error
                    name: resource.name,
                    // @ts-expect-error
                    parentResourceBuilderId: resource.parentResourceBuilderId,
                    // @ts-expect-error
                    parentResourceIndex: resource.parentResourceIndex,
                    // @ts-expect-error
                    resourceCategoryIndexes: resource.resourceCategoryIndexes,
                });
            })(resourceCategoryResources);
            dispatch(createAction(RESOURCES_UPDATED, resources));
        })(categories);
    };

const importEnterpriseSettings =
    (enterprises: EnterpriseImport[]): CommanderThunkAction =>
    (dispatch: CommanderThunkDispatch, getState) => {
        const settings = mapEnterpriseSettings(getState())(enterprises)[0];

        dispatch(addSettings(settings));
    };

export const processFile =
    (values: { files: FileList }, form: string): CommanderThunkAction =>
    (dispatch: CommanderThunkDispatch) => {
        const file = getFirstFile(values) as File;
        dispatch(createAction(REDUCERS_RESET));

        void getFileDataObject(file)
            .then((result) => {
                const { enterprises, resourceCategories, resources } = normalizeResponseData(result);

                dispatch(importEnterpriseSettings(enterprises));
                dispatch(importCategories(resourceCategories));
                dispatch(importResources(resources));

                dispatch(createAction(JSON_IMPORTED));
                dispatch(reset(form));
                dispatch(changeStep(EnterpriseBuilderScreenStep.Enterprise));
            })
            .catch((error) => {
                showErrorAlert({ description: <>{error.message}</> });
            });
    };
